// src/components/MapView.js
import React, { useRef, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

function getIconForCategory(category = '') {
  let iconPath = '/blattgrün.svg';

  const c = String(category).toLowerCase();

  if (c.includes('verkaufen') && c.includes('tauschen')) {
    iconPath = '/blattgelborange.svg';
  } else if (c.includes('verschenken') && c.includes('tauschen')) {
    iconPath = '/blattgrünorange.svg';
  } else if (c.includes('verkaufen')) {
    iconPath = '/blattgelb.svg';
  } else if (c.includes('tauschen')) {
    iconPath = '/blattorange.svg';
  }

  return new L.Icon({
    iconUrl: iconPath,
    iconSize: [38, 38],
    iconAnchor: [19, 38],
    popupAnchor: [0, -38],
    className: 'custom-leaf-icon',
  });
}

function normalizeCoords(offer) {
  // akzeptiert: offer.coords = [lat,lng] ODER {lat,lng}
  if (!offer) return null;

  if (Array.isArray(offer.coords) && offer.coords.length === 2) {
    const lat = Number(offer.coords[0]);
    const lng = Number(offer.coords[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }

  if (offer.coords && typeof offer.coords === 'object') {
    const lat = Number(offer.coords.lat);
    const lng = Number(offer.coords.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }

  // falls jemand lat/lng direkt speichert
  if (offer.lat != null && offer.lng != null) {
    const lat = Number(offer.lat);
    const lng = Number(offer.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }

  return null;
}

function MapStateListener({ onMapStateChange }) {
  useMapEvents({
    moveend(e) {
      if (!onMapStateChange) return;

      const map = e.target;
      const center = map.getCenter();
      const zoom = map.getZoom();

      onMapStateChange({
        center: [center.lat, center.lng],
        zoom,
      });
    },
  });

  return null;
}

function MapRefSetter({ mapRef }) {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;

    return () => {
      if (mapRef.current === map) {
        mapRef.current = null;
      }
    };
  }, [map, mapRef]);

  return null;
}

/**
 * ✅ NEU: “Jitter”-Markers
 * - Gruppiert Angebote nach identischen Koordinaten
 * - Verteilt Marker in einem kleinen Kreis (Pixel-Offset) um den Punkt
 * - Dadurch liegen Marker nebeneinander statt übereinander
 */
function JitteredMarkers({ offersWithCoords }) {
  const navigate = useNavigate();

  // Gruppieren nach Koordinaten-Key (leicht gerundet, damit 48.1234567 vs 48.1234568 nicht getrennt wird)
  const grouped = useMemo(() => {
    const m = new Map();
    (offersWithCoords || []).forEach((o) => {
      const [lat, lng] = o.__coords;
      const key = `${lat.toFixed(6)}|${lng.toFixed(6)}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(o);
    });

    // Stabil sortieren, damit Marker nicht “springen”
    for (const [k, list] of m.entries()) {
      list.sort((a, b) => String(a.id).localeCompare(String(b.id)));
      m.set(k, list);
    }

    return m;
  }, [offersWithCoords]);


  const jitterPosition = (latlng, index, total) => {
  if (!total || total <= 1) return latlng;

  const [lat, lng] = latlng;

  // Winkel im Kreis
  const angle = (2 * Math.PI * index) / total;

  // Abstand in METERN (klein halten!)
  // bei vielen Markern leicht erhöhen, aber gedeckelt
  const meters = Math.min(35, 12 + Math.max(0, total - 2) * 4); // 12m, 16m, 20m, ...

  // Umrechnung Meter -> Grad
  // 1° lat ~ 111.320 m
  const dLat = (meters * Math.cos(angle)) / 111320;

  // 1° lng hängt von Breitengrad ab
  const dLng =
    (meters * Math.sin(angle)) / (111320 * Math.cos((lat * Math.PI) / 180));

  return [lat + dLat, lng + dLng];
};

  const markers = [];
  for (const [, list] of grouped.entries()) {
    const total = list.length;
    list.forEach((offer, idx) => {
      const base = offer.__coords; // [lat,lng]
      const pos = jitterPosition(base, idx, total);

      markers.push(
        <Marker
  key={offer.id}
  position={pos}
  icon={getIconForCategory(offer.category)}
  eventHandlers={{
    click: (e) => {
      e.originalEvent?.preventDefault?.();
      e.originalEvent?.stopPropagation?.();
    },
  }}
>
  <Popup className="edens-popup">
    <div
      className="popup-box"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <h4>{offer.title}</h4>

      <p>PLZ: {offer.plz || "—"}</p>

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          setTimeout(() => {
            navigate(`/angebot/${offer.id}`);
          }, 50);
        }}
      >
        Details anzeigen
      </button>
    </div>
  </Popup>
</Marker>
      );
    });
  }

  return <>{markers}</>;
}

function MapView({
  offers = [],
  hideLegend = false,
  centerOverride = null,
  zoomOverride = 12,
  forceResize = false,
  centerKey = 0,
  onMapStateChange = null,
}) {
  const mapRef = useRef(null);
  const wrapperRef = useRef(null);

  // Marker nur für Offers mit gültigen coords
  const offersKey = useMemo(() => {
  return (offers || [])
    .map((o) => `${o.id}|${o.lat}|${o.lng}|${JSON.stringify(o.coords)}|${o.category}`)
    .join(";");
}, [offers]);

const offersWithCoords = useMemo(() => {
  return (offers || [])
    .map((o) => ({ ...o, __coords: normalizeCoords(o) }))
    .filter((o) => !!o.__coords);
}, [offersKey, offers]);

  // ✅ (optional) zusätzlicher setView-Fallback via mapRef – bleibt drin
  useEffect(() => {
  if (!mapRef.current) return;
  if (!centerOverride || !Array.isArray(centerOverride)) return;

  const map = mapRef.current;
  const current = map.getCenter();

  const samePosition =
    Math.abs(current.lat - centerOverride[0]) < 0.0001 &&
    Math.abs(current.lng - centerOverride[1]) < 0.0001;

  if (samePosition) return;

  try {
    map.setView(centerOverride, zoomOverride || map.getZoom() || 12, {
      animate: true,
    });
  } catch (e) {
    console.warn("Map setView failed:", e);
  }
}, [centerKey]);

  // ResizeObserver + optional forceResize
  useEffect(() => {
  let timeout;

  const resizeMap = () => {
    if (!mapRef.current) return;

    clearTimeout(timeout);
    timeout = setTimeout(() => {
      mapRef.current.invalidateSize();
    }, 200);
  };

  const initialTimeout = setTimeout(resizeMap, 350);

  const observer = new ResizeObserver(resizeMap);

  if (wrapperRef.current) {
    observer.observe(wrapperRef.current);
  }

  return () => {
    clearTimeout(timeout);
    clearTimeout(initialTimeout);
    observer.disconnect();
  };
}, []);

  useEffect(() => {
    if (!forceResize) return;
    if (!mapRef.current) return;
    setTimeout(() => mapRef.current.invalidateSize(), 250);
  }, [forceResize]);

  const initialCenter = centerOverride || [52.52, 13.405]; // Berlin fallback

  return (
    <div className="map-wrapper" ref={wrapperRef}>
      <MapContainer
  center={initialCenter}
  zoom={zoomOverride || 12}
  className="leaflet-container-custom"
>
  <MapRefSetter mapRef={mapRef} />
        <TileLayer
          attribution="&copy; OpenStreetMap-Mitwirkende"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
 

<MapStateListener onMapStateChange={onMapStateChange} />

<JitteredMarkers offersWithCoords={offersWithCoords} />
      </MapContainer>

      {!hideLegend && (
        <div className="map-legend">
          <strong>Legende:</strong>
          <ul>
            <li>
              <img src="/blattgrün.svg" alt="Verschenken" /> Verschenken
            </li>
            <li>
              <img src="/blattorange.svg" alt="Tauschen" /> Tauschen
            </li>
            <li>
              <img src="/blattgelb.svg" alt="Verkaufen" /> Verkaufen
            </li>
            <li>
              <img src="/blattgrünorange.svg" alt="Verschenken oder Tauschen" /> Verschenken oder Tauschen
            </li>
            <li>
              <img src="/blattgelborange.svg" alt="Verkaufen oder Tauschen" /> Verkaufen oder Tauschen
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default MapView;